/**
 * Centralized Zod Validation Schemas
 *
 * This file contains all reusable validation schemas for forms throughout the application.
 * Error messages are automatically translated via the Zod error map (i18n/zodErrorMap.ts).
 *
 * Usage:
 * ```tsx
 * import { loginSchema, registerSchema } from '@/lib/validationSchemas';
 *
 * const form = useForm({
 *   resolver: zodResolver(loginSchema),
 * });
 * ```
 */

import { z } from 'zod';

// ============================================================================
// Base Field Schemas (reusable primitives)
// ============================================================================

/**
 * Email validation
 * Requires valid email format
 */
export const emailSchema = z
  .string()
  .min(1) // Required
  .email(); // Valid email format

/**
 * Password validation
 * Minimum 8 characters, maximum 100 characters
 */
export const passwordSchema = z
  .string()
  .min(8)
  .max(100);

/**
 * Name validation
 * Minimum 2 characters, maximum 100 characters
 */
export const nameSchema = z
  .string()
  .min(1) // Required
  .min(2)
  .max(100);

/**
 * URL validation
 * Requires valid URL format
 */
export const urlSchema = z
  .string()
  .min(1) // Required
  .url();

/**
 * Campaign name validation
 * Minimum 3 characters, maximum 100 characters
 */
export const campaignNameSchema = z
  .string()
  .min(1) // Required
  .min(3)
  .max(100);

// ============================================================================
// Authentication Schemas
// ============================================================================

/**
 * Login form validation schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1), // No min length for login
});

export type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Registration form validation schema
 * Includes password confirmation matching and optional device fingerprint
 */
export const registerSchema = z
  .object({
    name: nameSchema,
    email: emailSchema,
    password: passwordSchema,
    password_confirmation: z.string().min(1),
    accept_terms: z.boolean().refine((val) => val === true, {
      message: 'You must accept the Terms and Conditions',
    }),
    referral_code: z.string().optional(),
    device_fingerprint: z.string().regex(/^[a-f0-9]{64}$/i).optional().nullable(),
    telegram: z
      .string()
      .min(1, 'validation:custom.telegram_required')
      .regex(/^@?[a-zA-Z0-9_]{5,32}$/, 'validation:invalid_telegram'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'validation:passwords_must_match',
    path: ['password_confirmation'],
  });

export type RegisterFormData = z.infer<typeof registerSchema>;

/**
 * Forgot password form validation schema
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

/**
 * Reset password form validation schema
 * Includes password confirmation matching
 */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1),
    email: emailSchema,
    password: passwordSchema,
    password_confirmation: z.string().min(1),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'validation:passwords_must_match',
    path: ['password_confirmation'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

// ============================================================================
// Campaign Schemas
// ============================================================================

/**
 * Campaign filter validation schema
 * Defines targeting rules for country, language, and OS
 */
export const campaignFilterSchema = z.object({
  allowed_countries: z.array(z.string().length(2)).optional().nullable(),
  blocked_countries: z.array(z.string().length(2)).optional().nullable(),
  allowed_languages: z.array(z.string().length(2)).optional().nullable(),
  blocked_languages: z.array(z.string().length(2)).optional().nullable(),
  allowed_os: z.array(z.string().max(50)).optional().nullable(),
  blocked_os: z.array(z.string().max(50)).optional().nullable(),
});

export type CampaignFilterData = z.infer<typeof campaignFilterSchema>;

/**
 * Domain validation schema
 * Must be in format: domain.extension (no www, no protocol)
 */
export const domainSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^(?!www\.)([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, {
    message: 'Domain must be in format: domain.com (no www or https://)',
  });

/**
 * Campaign form validation schema
 * Used for both create and edit operations
 */
export const campaignSchema = z
  .object({
    name: campaignNameSchema,
    human_redirect_url: z.string().optional().default(''),
    human_redirect_type: z.enum(['url', 'path']).default('url'),
    human_redirect_mode: z.enum(['require', 'iframe', 'redirect']).default('redirect'),
    human_back_redirect_url: z.string().optional().nullable(),
    bot_redirect_url: z.string().optional().default(''),
    bot_redirect_type: z.enum(['url', 'path']).default('url'),
    domains: z.array(domainSchema).min(1, { message: 'At least one domain is required' }),
    target_link: urlSchema.optional().nullable(),
    content_mode: z.number().int().min(1).max(5).optional().nullable(),
    pass_query_params: z.boolean().optional().nullable(),
    partial_integration: z.boolean().optional().nullable(),
    disable_blacklist: z.boolean().optional().nullable(),
    postback_param: z.string().max(100).optional().nullable(),
    query_string: z.string().max(500).optional().nullable(),
    status: z.enum(['active', 'paused', 'archived']).optional(),
    operation_mode: z.enum(['filter', 'monitor']).optional(),
    assigned_manager_id: z.number().nullable().optional(),
    filter: campaignFilterSchema.optional().nullable(),
    tags: z.array(z.any()).optional().nullable(),
    target_mode: z.enum(['default', 'split', 'split_params', 'split_params_percnt']).optional().nullable(),
    split_links: z.array(z.object({
      id: z.string(),
      name: z.string().optional().nullable(),
      url: z.string().min(1, 'URL is required'),
      weight: z.number().int().optional().nullable(),
      conditions: z.any().optional().nullable(),
    })).max(5).optional().nullable(),
  })
  .refine(
    (data) => {
      // Monitor Only campaigns never redirect, so the human page URL is optional.
      if (data.operation_mode === 'monitor') return true;
      if (data.human_redirect_type === 'url') {
        try { new URL(data.human_redirect_url ?? ''); return true; } catch { return false; }
      }
      return !!data.human_redirect_url && data.human_redirect_url.length > 0;
    },
    {
      message: 'Valid redirect URL or file path is required for human traffic',
      path: ['human_redirect_url'],
    }
  )
  .refine(
    (data) => {
      // Monitor Only campaigns never redirect, so the bot page URL is optional.
      if (data.operation_mode === 'monitor') return true;
      if (data.bot_redirect_type === 'url') {
        try { new URL(data.bot_redirect_url ?? ''); return true; } catch { return false; }
      }
      return !!data.bot_redirect_url && data.bot_redirect_url.length > 0;
    },
    {
      message: 'Valid redirect URL or file path is required for bot traffic',
      path: ['bot_redirect_url'],
    }
  );

export type CampaignFormData = z.infer<typeof campaignSchema>;

// ============================================================================
// User Profile Schemas
// ============================================================================

/**
 * Profile update validation schema
 */
export const profileUpdateSchema = z.object({
  name: nameSchema,
});

export type ProfileUpdateFormData = z.infer<typeof profileUpdateSchema>;

/**
 * Password change validation schema
 * Requires current password and matching new passwords
 */
export const createPasswordChangeSchema = (t: (key: string) => string) =>
  z
    .object({
      current_password: z.string().min(1),
      password: passwordSchema,
      password_confirmation: z.string().min(1),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: t('validation:passwords_must_match'),
      path: ['password_confirmation'],
    });

export type PasswordChangeFormData = z.infer<
  ReturnType<typeof createPasswordChangeSchema>
>;

// ============================================================================
// Team Management Schemas
// ============================================================================

/**
 * Team invitation form validation schema
 */
export const teamInvitationSchema = z.object({
  email: emailSchema,
  role: z.enum(['manager']),
});

export type TeamInvitationFormData = z.infer<typeof teamInvitationSchema>;

// ============================================================================
// Account Settings Schemas
// ============================================================================

/**
 * Account settings update validation schema
 */
export const accountSettingsSchema = z.object({
  name: z
    .string()
    .min(1)
    .min(3)
    .max(100),
});

export type AccountSettingsFormData = z.infer<typeof accountSettingsSchema>;

/**
 * Account deletion confirmation schema
 */
export const accountDeletionSchema = z.object({
  password: z.string().min(1),
  confirmation: z.literal('DELETE'), // Must type exactly "DELETE"
});

export type AccountDeletionFormData = z.infer<typeof accountDeletionSchema>;

// ============================================================================
// Admin Schemas
// ============================================================================

/**
 * Manual subscription payment recording schema
 */
export const subscriptionPaymentSchema = z.object({
  account_id: z.number().positive(),
  plan_id: z.number().positive(),
  amount: z.number().positive(),
  currency: z.string().length(3), // ISO 4217 code (USD, EUR, etc.)
  payment_method: z.enum(['crypto', 'bank_transfer', 'manual']),
  payment_date: z.string().min(1), // ISO date string
  notes: z.string().optional(),
});

export type SubscriptionPaymentFormData = z.infer<
  typeof subscriptionPaymentSchema
>;

/**
 * FAQ question form validation schema
 */
export const faqQuestionSchema = z.object({
  category_id: z.number().positive(),
  question: z.string().min(5).max(500),
  answer: z.string().min(10).max(5000),
  order: z.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
});

export type FaqQuestionFormData = z.infer<typeof faqQuestionSchema>;

/**
 * FAQ category form validation schema
 */
export const faqCategorySchema = z.object({
  name: z.string().min(2).max(100),
  slug: z
    .string()
    .min(2)
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens'),
  order: z.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
});

export type FaqCategoryFormData = z.infer<typeof faqCategorySchema>;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Helper to extract form error messages for a given field
 * Useful for displaying validation errors in custom UI
 */
export function getFieldError<T extends Record<string, any>>(
  errors: any,
  field: keyof T
): string | undefined {
  return errors[field]?.message;
}

/**
 * Helper to check if a field has errors
 */
export function hasFieldError<T extends Record<string, any>>(
  errors: any,
  field: keyof T
): boolean {
  return !!errors[field];
}
